import React, { useState } from 'react';
import { ChevronRight, Pencil } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';

export const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-[140px_1fr] gap-4 items-baseline">
    <div className="text-xs uppercase tracking-wider font-medium text-neutral-500">{label}</div>
    <div className="text-sm text-neutral-900">{value}</div>
  </div>
);

export const DetailKV = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 py-2 items-baseline border-b border-neutral-100 last:border-0">
    <div className="text-xs text-neutral-500">{label}:</div>
    <div className="text-sm text-neutral-900">{value}</div>
  </div>
);

export const PropertyCard = ({
  label,
  value,
  unit,
}: {
  label: string;
  value: React.ReactNode;
  unit?: string;
}) => (
  <div className="grid grid-cols-[1fr_auto] gap-3 py-2 px-3 bg-white border border-neutral-150 rounded hover:border-neutral-200 transition-colors">
    <span className="text-sm text-neutral-700">{label}</span>
    <span className="font-mono text-sm text-neutral-900 tabular-nums">
      {value}
      {unit && <span className="text-neutral-500 ml-1.5">{unit}</span>}
    </span>
  </div>
);

export const CollapseSection = ({
  title,
  badge,
  defaultOpen = false,
  action,
  children,
}: {
  title: string;
  badge?: string | number;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const empty = !children;

  return (
    <div className="border-t border-neutral-150">
      <div className="flex items-center justify-between py-3">
        <button
          data-testid="detail-section-toggle-btn"
          onClick={() => !empty && setOpen((v) => !v)}
          className={cn(
            'flex items-center gap-2',
            empty ? 'cursor-default opacity-60' : 'hover:text-primary-700'
          )}
        >
          {!empty && (
            <ChevronRight
              size={14}
              className={cn('text-neutral-400 transition-transform', open && 'rotate-90')}
            />
          )}
          {empty && <span className="w-3.5" />}
          <span className="text-sm font-semibold text-neutral-900 tracking-tight">{title}</span>
          {badge != null && (
            <Badge variant="outline" size="sm">
              {badge}
            </Badge>
          )}
        </button>
        <div className="flex items-center gap-1.5">
          {action}
          {empty && <span className="text-xs text-neutral-500">Bez položek</span>}
        </div>
      </div>
      {open && children && <div className="pb-3">{children}</div>}
    </div>
  );
};

export const Section = ({
  title,
  badge,
  onEdit,
  children,
}: {
  title: string;
  badge?: string;
  onEdit?: () => void;
  children: React.ReactNode;
}) => (
  <div className="border-b border-neutral-150 py-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-neutral-900 tracking-tight">{title}</h3>
      <div className="flex items-center gap-2">
        {badge && (
          <Badge variant="outline" size="sm">
            {badge}
          </Badge>
        )}
        {onEdit && (
          <Button
            data-testid="detail-section-edit-btn"
            variant="ghost"
            size="icon-sm"
            onClick={onEdit}
          >
            <Pencil size={12} />
          </Button>
        )}
      </div>
    </div>
    {children}
  </div>
);

export const SectionHeader = ({
  title,
  badge,
  onEdit,
  extra,
}: {
  title: string;
  badge?: string | number;
  onEdit?: () => void;
  extra?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between pb-4 border-b border-neutral-150">
    <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
    <div className="flex items-center gap-1.5">
      {badge != null && (
        <Badge variant="outline" size="sm" className="font-mono">
          {badge}
        </Badge>
      )}
      {extra}
      {onEdit && (
        <Button
          data-testid="section-header-edit-btn"
          variant="ghost"
          size="icon-sm"
          onClick={onEdit}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            <path d="m15 5 4 4" />
          </svg>
        </Button>
      )}
    </div>
  </div>
);
